import { useEffect, useMemo, useState } from 'react';

function PlayIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="5 3 19 12 5 21 5 3" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function fmtTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString('es-MX', { hour12: false });
}

const TYPES = [
  { id: 'summary', label: 'Resumen' },
  { id: 'trips', label: 'Viajes' },
  { id: 'overspeed', label: 'Excesos de velocidad' },
  { id: 'stops', label: 'Paradas' },
  { id: 'mileage', label: 'Kilometraje' },
  { id: 'alarms', label: 'Alarmas' },
  { id: 'commands', label: 'Comandos' },
  { id: 'fuel', label: 'Combustible' },
];

const COLUMNS = {
  trips: [
    { key: 'start', label: 'Inicio' },
    { key: 'end', label: 'Fin' },
    { key: 'distanceKm', label: 'Distancia (km)' },
    { key: 'points', label: 'Puntos' },
  ],
  overspeed: [
    { key: 'uniqueid', label: 'Dispositivo' },
    { key: 'devicetime', label: 'Hora' },
    { key: 'speedKmh', label: 'Velocidad (km/h)' },
    { key: 'latitude', label: 'Latitud' },
    { key: 'longitude', label: 'Longitud' },
  ],
  stops: [
    { key: 'uniqueid', label: 'Dispositivo' },
    { key: 'start', label: 'Inicio' },
    { key: 'end', label: 'Fin' },
    { key: 'minutes', label: 'Minutos' },
    { key: 'latitude', label: 'Latitud' },
    { key: 'longitude', label: 'Longitud' },
  ],
  mileage: [
    { key: 'uniqueid', label: 'Dispositivo' },
    { key: 'distanceKm', label: 'Distancia (km)' },
  ],
  alarms: [
    { key: 'device_id', label: 'Dispositivo' },
    { key: 'type', label: 'Tipo' },
    { key: 'message', label: 'Mensaje' },
    { key: 'created_at', label: 'Hora' },
  ],
  commands: [
    { key: 'device_id', label: 'Dispositivo' },
    { key: 'type', label: 'Tipo' },
    { key: 'status', label: 'Estado' },
    { key: 'created_at', label: 'Enviado' },
  ],
  fuel: [
    { key: 't', label: 'Hora' },
    { key: 'fuel', label: 'Combustible (%)' },
    { key: 'batt', label: 'Batería (V)' },
    { key: 'ignition', label: 'Ignición' },
  ],
};

function cellValue(row, key) {
  const v = row[key];
  if (v == null) return '';
  if (key === 'created_at' || key === 'devicetime' || key === 'start' || key === 'end' || key === 't') return fmtTime(v);
  if (typeof v === 'boolean') return v ? 'Sí' : 'No';
  return String(v);
}

export default function Reportes({ mapDevices, authHeaders, initialDevice = '' }) {
  const [type, setType] = useState('summary');
  const [device, setDevice] = useState(initialDevice || '');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [sort, setSort] = useState(null);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(false);

  function qs() {
    const p = new URLSearchParams();
    if (device) p.set('deviceId', device);
    if (from) p.set('from', new Date(from).toISOString());
    if (to) p.set('to', new Date(to).toISOString());
    return p.toString();
  }

  async function load() {
    setLoading(true);
    setSort(null);
    try {
      const h = authHeaders();
      if (type === 'summary' || type === 'trips') {
        if (!device) { setSummary(null); setRows([]); setLoading(false); return; }
        const res = await fetch('/api/reports?' + qs(), { headers: h });
        const data = await res.json();
        if (type === 'summary') { setSummary(data); setRows(data && data.trips ? data.trips : []); }
        else { setSummary(null); setRows(data && data.trips ? data.trips : []); }
      } else if (type === 'overspeed') {
        const res = await fetch('/api/reports/overspeed?' + qs(), { headers: h });
        setRows(await res.json());
      } else if (type === 'stops') {
        const res = await fetch('/api/reports/stops?' + qs(), { headers: h });
        setRows(await res.json());
      } else if (type === 'mileage') {
        const res = await fetch('/api/reports/mileage?' + qs(), { headers: h });
        setRows(await res.json());
      } else if (type === 'alarms') {
        const res = await fetch('/api/alerts?limit=1000' + (device ? '&deviceId=' + encodeURIComponent(device) : ''), { headers: h });
        setRows(await res.json());
      } else if (type === 'commands') {
        const res = await fetch('/api/commands?limit=1000' + (device ? '&deviceId=' + encodeURIComponent(device) : ''), { headers: h });
        setRows(await res.json());
      } else if (type === 'fuel') {
        const res = await fetch('/api/fuel?deviceId=' + encodeURIComponent(device || ''), { headers: h });
        setRows(await res.json());
      }
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { setSummary(null); setRows([]); }, [type]);

  const columns = COLUMNS[type] || [];

  const processed = useMemo(() => {
    let list = rows;
    if (filter.trim()) {
      const f = filter.trim().toLowerCase();
      list = list.filter((r) => columns.some((c) => cellValue(r, c.key).toLowerCase().includes(f)));
    }
    if (sort) {
      list = [...list].sort((a, b) => {
        const av = cellValue(a, sort.key);
        const bv = cellValue(b, sort.key);
        const an = parseFloat(av), bn = parseFloat(bv);
        const cmp = (!isNaN(an) && !isNaN(bn)) ? an - bn : String(av).localeCompare(String(bv));
        return sort.dir === 'asc' ? cmp : -cmp;
      });
    }
    return list;
  }, [rows, filter, sort, columns]);

  function toggleSort(key) {
    setSort((s) => (s && s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  }

  function downloadCsv() {
    if (!processed.length) return;
    const header = columns.map((c) => c.label);
    const lines = processed.map((r) => columns.map((c) => '"' + String(rowToRaw(r, c.key)).replace(/"/g, '""') + '"'));
    const csv = [header.join(','), ...lines.map((l) => l.join(','))].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `reporte-${type}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  function rowToRaw(r, key) {
    const v = r[key];
    if (v == null) return '';
    if (key === 'created_at' || key === 'devicetime' || key === 'start' || key === 'end' || key === 't') return fmtTime(v);
    return typeof v === 'object' ? JSON.stringify(v) : String(v);
  }

  return (
    <div className="manual">
      <section className="card">
        <h2>Reportes</h2>
        <div className="rep-tabs">
          {TYPES.map((t) => (
            <button key={t.id} className={type === t.id ? 'active' : ''} onClick={() => setType(t.id)}>{t.label}</button>
          ))}
        </div>
        <div className="cmd-form">
          <select value={device} onChange={(e) => setDevice(e.target.value)}>
            <option value="">
              {(type === 'summary' || type === 'trips' || type === 'fuel') ? '— Dispositivo —' : '— Todos los dispositivos —'}
            </option>
            {mapDevices.map((d) => <option key={d.id} value={d.uniqueid}>{d.uniqueid || d.name}</option>)}
          </select>
          {(type === 'summary' || type === 'trips' || type === 'overspeed' || type === 'stops' || type === 'mileage') && (
            <>
              <input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} />
              <input type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} />
            </>
          )}
          <button className="mini icon" onClick={load} disabled={loading || (['summary', 'trips', 'fuel'].includes(type) && !device)} title="Generar reporte">
            <PlayIcon />
          </button>
          <button className="mini icon" onClick={downloadCsv} disabled={!processed.length} title="Descargar CSV"><DownloadIcon /></button>
        </div>
      </section>

      {type === 'summary' && summary && (
        <section className="stats report-stats">
          <div className="card stat"><div className="stat-value">{summary.totalDistanceKm}</div><div className="stat-label">Distancia (km)</div></div>
          <div className="card stat"><div className="stat-value">{summary.maxSpeedKmh}</div><div className="stat-label">Vel. máx (km/h)</div></div>
          <div className="card stat"><div className="stat-value">{summary.avgSpeedKmh}</div><div className="stat-label">Vel. media (km/h)</div></div>
          <div className="card stat"><div className="stat-value">{summary.numTrips}</div><div className="stat-label">Viajes</div></div>
          <div className="card stat"><div className="stat-value">{summary.durationMinutes}</div><div className="stat-label">Duración (min)</div></div>
          <div className="card stat"><div className="stat-value">{summary.numStops}</div><div className="stat-label">Paradas</div></div>
          <div className="card stat"><div className="stat-value">{summary.stoppedMinutes}</div><div className="stat-label">Parado (min)</div></div>
        </section>
      )}

      <section className="card panel">
        <div className="feed-head">
          <h2>{TYPES.find((t) => t.id === type)?.label} ({processed.length})</h2>
          <input type="text" placeholder="Filtrar..." value={filter} onChange={(e) => setFilter(e.target.value)} />
        </div>
        <table>
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} style={{ cursor: 'pointer' }} onClick={() => toggleSort(c.key)}>
                  {c.label}{sort && sort.key === c.key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {processed.length === 0 && <tr><td colSpan={columns.length} className="empty">Sin datos</td></tr>}
            {processed.map((r, i) => (
              <tr key={i}>
                {columns.map((c) => <td key={c.key} className={c.key === 'uniqueid' || c.key === 'device_id' ? 'mono' : ''}>{cellValue(r, c.key)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
